const namespaced = true

const config = require('../../../config')
const axios = require('axios')
const checkUrl = 'https://' + config.short_name + '.planner.documedias.systems/api/app/check'

const state = {}

// Shared helper: process raw data tables into counts + session/presentation details
// options.filterDeleted: if true, exclude entries where deleted_at is not null
function processData(data, options = {}) {
    // Track deleted IDs per entity type when filtering
    const deletedIds = {
        sessions: new Set(),
        presentations: new Set(),
        persons: new Set(),
    }

    const filterActive = (arr, entityType) => {
        if (!options.filterDeleted) return arr
        const active = []
        for (let i = 0; i < arr.length; i++) {
            if (arr[i].deleted_at) {
                if (entityType && deletedIds[entityType]) {
                    deletedIds[entityType].add(arr[i].id)
                }
            } else {
                active.push(arr[i])
            }
        }
        return active
    }

    const sessions = filterActive(data['sessions'] || [], 'sessions')
    const presentations = filterActive(data['presentations'] || [], 'presentations')
    const persons = filterActive(data['persons'] || [], 'persons')
    const abstracts = filterActive(data['abstracts'] || [])
    const abstractLinks = filterActive(data['presentation_abstract_links'] || [])
    const personRoles = filterActive(data['person_roles'] || [])
    const sessionPresLinks = filterActive(data['session_presentation_links'] || [])
    const sessionTypeLinks = filterActive(data['session_sessiontype_links'] || [])
    const sessionTypes = filterActive(data['sessiontypes'] || [])
    const locations = filterActive(data['locations'] || [])
    const locationLinks = filterActive(data['session_location_links'] || [])

    // Build lookup maps
    const sessionTypesMap = new Map(sessionTypes.map(t => [t.id, t]))
    const locationsMap = new Map(locations.map(l => [l.id, l]))
    const personsMap = new Map(persons.map(p => [p.id, p]))

    // Count abstracts allocated in the programme
    // Two structures supported:
    //   Local DB: presentation_abstract_links has {abstract_id, presentation_id}
    //   API: presentation_abstract_links ARE the abstracts (have title, authors), 
    //        presentations have abstract_id pointing to them
    const sessionsSet = new Set(sessions.map(s => s.id))
    const presWithSession = new Set()
    for (let i = 0; i < sessionPresLinks.length; i++) {
        if (sessionsSet.has(sessionPresLinks[i].session_id)) {
            presWithSession.add(sessionPresLinks[i].presentation_id)
        }
    }

    let allocatedAbstractCount = 0
    // Check if abstractLinks have presentation_id (local DB link table format)
    const isLocalLinkFormat = abstractLinks.length > 0 && abstractLinks[0].presentation_id
    if (isLocalLinkFormat) {
        // Local DB: abstractLinks = {abstract_id, presentation_id}
        for (let i = 0; i < abstractLinks.length; i++) {
            if (presWithSession.has(abstractLinks[i].presentation_id)) {
                allocatedAbstractCount++
            }
        }
    } else {
        // API: abstractLinks ARE abstracts, presentations.abstract_id links to them
        const abstractIds = new Set(abstractLinks.map(a => a.id))
        for (let i = 0; i < presentations.length; i++) {
            const pres = presentations[i]
            if (pres.abstract_id && abstractIds.has(pres.abstract_id) && presWithSession.has(pres.id)) {
                allocatedAbstractCount++
            }
        }
    }

    // Group presentation links by session_id
    const presLinksBySession = new Map()
    for (let i = 0; i < sessionPresLinks.length; i++) {
        const link = sessionPresLinks[i]
        if (!presLinksBySession.has(link.session_id)) {
            presLinksBySession.set(link.session_id, [])
        }
        presLinksBySession.get(link.session_id).push(link)
    }

    // Group session roles by session_id
    const chairsBySession = new Map()
    for (let i = 0; i < personRoles.length; i++) {
        const role = personRoles[i]
        if (role.session_id) {
            if (!chairsBySession.has(role.session_id)) {
                chairsBySession.set(role.session_id, [])
            }
            chairsBySession.get(role.session_id).push(role)
        }
    }

    // Group presentation roles by presentation_id
    const speakersByPresentation = new Map()
    for (let i = 0; i < personRoles.length; i++) {
        const role = personRoles[i]
        if (role.presentation_id) {
            if (!speakersByPresentation.has(role.presentation_id)) {
                speakersByPresentation.set(role.presentation_id, [])
            }
            speakersByPresentation.get(role.presentation_id).push(role)
        }
    }

    // Session type link map
    const typeBySession = new Map()
    for (let i = 0; i < sessionTypeLinks.length; i++) {
        typeBySession.set(sessionTypeLinks[i].session_id, sessionTypeLinks[i].sessiontype_id)
    }

    // Location link map
    const locationBySession = new Map()
    for (let i = 0; i < locationLinks.length; i++) {
        locationBySession.set(locationLinks[i].session_id, locationLinks[i].location_id)
    }

    // Build per-session details
    const sessionDetails = []
    for (let i = 0; i < sessions.length; i++) {
        const session = sessions[i]
        const presLinks = presLinksBySession.get(session.id) || []
        const chairs = chairsBySession.get(session.id) || []
        const typeId = typeBySession.get(session.id)
        const sessionType = typeId ? sessionTypesMap.get(typeId) : null
        const locId = locationBySession.get(session.id)
        const location = locId ? locationsMap.get(locId) : null

        // Resolve chair names
        const chairNames = []
        for (let c = 0; c < chairs.length; c++) {
            const person = personsMap.get(chairs[c].person_id)
            chairNames.push(person ? person.display_name : '(unknown)')
        }

        sessionDetails.push({
            id: session.id,
            title: session.title || '(no title)',
            code: session.code || '',
            start_time: session.start_time || '',
            end_time: session.end_time || '',
            sessionType: sessionType ? sessionType.name : '—',
            location: location ? location.name : '—',
            presentationCount: presLinks.length,
            chairCount: chairs.length,
            chairNames: chairNames.sort(),
            external_link: session.external_link || '',
        })
    }

    // Build per-presentation details
    const presentationsMap = new Map(presentations.map(p => [p.id, p]))
    const presentationDetails = []
    for (let i = 0; i < sessionPresLinks.length; i++) {
        const link = sessionPresLinks[i]
        const pres = presentationsMap.get(link.presentation_id)
        if (!pres) continue
        const session = sessions.find(s => s.id === link.session_id)
        const speakers = speakersByPresentation.get(pres.id) || []

        // Resolve speaker names
        const speakerNames = []
        for (let s = 0; s < speakers.length; s++) {
            const person = personsMap.get(speakers[s].person_id)
            speakerNames.push(person ? person.display_name : '(unknown)')
        }

        presentationDetails.push({
            id: pres.id,
            title: pres.title || '(no title)',
            code: pres.code || '',
            sessionTitle: session ? (session.title || '(no title)') : '—',
            sessionId: link.session_id,
            speakerCount: speakers.length,
            speakerNames: speakerNames.sort(),
        })
    }

    // Build per-person details
    const personDetails = []
    for (let i = 0; i < persons.length; i++) {
        const p = persons[i]
        personDetails.push({
            id: p.id,
            display_name: p.display_name || '(no name)',
        })
    }

    return {
        counts: {
            sessions: sessions.length,
            presentations: presentationDetails.length,
            persons: persons.length,
            abstracts: allocatedAbstractCount,
            personRoles: personRoles.length,
        },
        sessionDetails,
        presentationDetails,
        personDetails,
        deletedIds,
    }
}

// Compare app vs api data and return list of conflicts
// apiDeletedIds: { sessions: Set, presentations: Set, persons: Set } — IDs deleted in API
function detectConflicts(appData, apiData, apiDeletedIds) {
    const conflicts = []
    const deleted = apiDeletedIds || { sessions: new Set(), presentations: new Set(), persons: new Set() }

    // Build lookup maps for API data
    const apiSessionMap = new Map(apiData.sessionDetails.map(s => [s.id, s]))
    const apiPresMap = new Map()
    for (let i = 0; i < apiData.presentationDetails.length; i++) {
        const p = apiData.presentationDetails[i]
        // Key by id + sessionId to handle same pres in multiple sessions
        apiPresMap.set(p.id + '_' + p.sessionId, p)
    }

    // Session-level conflicts
    for (let i = 0; i < appData.sessionDetails.length; i++) {
        const appSess = appData.sessionDetails[i]
        const apiSess = apiSessionMap.get(appSess.id)

        if (!apiSess) {
            const isDeletedInApi = deleted.sessions.has(appSess.id)
            conflicts.push({
                type: 'session',
                severity: 'error',
                id: appSess.id,
                code: appSess.code,
                title: appSess.title,
                field: 'existence',
                description: isDeletedInApi
                    ? 'Session is deleted in API but still active in app'
                    : 'Session exists in app but not in API',
                appValue: 'present',
                apiValue: isDeletedInApi ? 'deleted' : 'missing',
            })
            continue
        }

        if (appSess.presentationCount !== apiSess.presentationCount) {
            conflicts.push({
                type: 'session',
                severity: 'warning',
                id: appSess.id,
                code: appSess.code,
                title: appSess.title,
                field: 'presentationCount',
                description: 'Presentation count mismatch',
                appValue: appSess.presentationCount,
                apiValue: apiSess.presentationCount,
            })
        }

        if (appSess.chairCount !== apiSess.chairCount) {
            conflicts.push({
                type: 'session',
                severity: 'warning',
                id: appSess.id,
                code: appSess.code,
                title: appSess.title,
                field: 'chairCount',
                description: 'Chair count mismatch',
                appValue: appSess.chairCount,
                apiValue: apiSess.chairCount,
            })
        }

        // Compare chair names
        const appChairs = appSess.chairNames.join(', ')
        const apiChairs = apiSess.chairNames.join(', ')
        if (appChairs !== apiChairs) {
            conflicts.push({
                type: 'session',
                severity: 'warning',
                id: appSess.id,
                code: appSess.code,
                title: appSess.title,
                field: 'chairNames',
                description: 'Chair names differ',
                appValue: appChairs || '(none)',
                apiValue: apiChairs || '(none)',
            })
        }

        apiSessionMap.delete(appSess.id)
    }

    // Sessions in API but not in app
    for (const [id, apiSess] of apiSessionMap) {
        conflicts.push({
            type: 'session',
            severity: 'error',
            id: id,
            code: apiSess.code,
            title: apiSess.title,
            field: 'existence',
            description: 'Session exists in API but not in app',
            appValue: 'missing',
            apiValue: 'present',
        })
    }

    // Presentation-level conflicts
    const appPresMap = new Map()
    for (let i = 0; i < appData.presentationDetails.length; i++) {
        const p = appData.presentationDetails[i]
        appPresMap.set(p.id + '_' + p.sessionId, p)
    }

    for (const [key, appPres] of appPresMap) {
        const apiPres = apiPresMap.get(key)

        if (!apiPres) {
            const isDeletedInApi = deleted.presentations.has(appPres.id)
            conflicts.push({
                type: 'presentation',
                severity: 'error',
                id: appPres.id,
                code: appPres.code,
                title: appPres.title,
                field: 'existence',
                description: isDeletedInApi
                    ? 'Presentation is deleted in API but still active in app'
                    : 'Presentation exists in app but not in API',
                appValue: 'present',
                apiValue: isDeletedInApi ? 'deleted' : 'missing',
            })
            continue
        }

        if (appPres.speakerCount !== apiPres.speakerCount) {
            conflicts.push({
                type: 'presentation',
                severity: 'warning',
                id: appPres.id,
                code: appPres.code,
                title: appPres.title,
                field: 'speakerCount',
                description: 'Speaker count mismatch',
                appValue: appPres.speakerCount,
                apiValue: apiPres.speakerCount,
            })
        }

        // Compare speaker names
        const appSpeakers = appPres.speakerNames.join(', ')
        const apiSpeakers = apiPres.speakerNames.join(', ')
        if (appSpeakers !== apiSpeakers) {
            conflicts.push({
                type: 'presentation',
                severity: 'warning',
                id: appPres.id,
                code: appPres.code,
                title: appPres.title,
                field: 'speakerNames',
                description: 'Speaker names differ',
                appValue: appSpeakers || '(none)',
                apiValue: apiSpeakers || '(none)',
            })
        }

        apiPresMap.delete(key)
    }

    // Presentations in API but not in app
    for (const [key, apiPres] of apiPresMap) {
        conflicts.push({
            type: 'presentation',
            severity: 'error',
            id: apiPres.id,
            code: apiPres.code,
            title: apiPres.title,
            field: 'existence',
            description: 'Presentation exists in API but not in app',
            appValue: 'missing',
            apiValue: 'present',
        })
    }

    // Person-level conflicts
    const apiPersonMap = new Map(apiData.personDetails.map(p => [p.id, p]))

    for (let i = 0; i < appData.personDetails.length; i++) {
        const appPerson = appData.personDetails[i]
        const apiPerson = apiPersonMap.get(appPerson.id)

        if (!apiPerson) {
            const isDeletedInApi = deleted.persons.has(appPerson.id)
            conflicts.push({
                type: 'person',
                severity: 'error',
                id: appPerson.id,
                code: '',
                title: appPerson.display_name,
                field: 'existence',
                description: isDeletedInApi
                    ? 'Person is deleted in API but still active in app'
                    : 'Person exists in app but not in API',
                appValue: 'present',
                apiValue: isDeletedInApi ? 'deleted' : 'missing',
            })
            continue
        }

        apiPersonMap.delete(appPerson.id)
    }

    // Persons in API but not in app
    for (const [id, apiPerson] of apiPersonMap) {
        conflicts.push({
            type: 'person',
            severity: 'error',
            id: id,
            code: '',
            title: apiPerson.display_name,
            field: 'existence',
            description: 'Person exists in API but not in app',
            appValue: 'missing',
            apiValue: 'present',
        })
    }

    return conflicts
}

const actions = {
    /**
     * Fetches all QC overview counts from local database.
     */
    async getQCData({dispatch}) {
        let data = await dispatch('database/getMultiple', [
            'sessions',
            'presentations',
            'persons',
            'presentation_abstract_links',
            'person_roles',
            'session_presentation_links',
            'abstracts',
            'session_sessiontype_links',
            'sessiontypes',
            'locations',
            'session_location_links'
        ], {root: true})

        return processData(data)
    },

    /**
     * Fetches data from external API and processes with same logic.
     */
    async getQCDataFromAPI() {
        const response = await axios.default.get(checkUrl)
        return processData(response.data, { filterDeleted: true })
    },

    /**
     * Compares app and API data, returns conflicts.
     */
    async getQCConflicts({dispatch}) {
        const [appData, apiData] = await Promise.all([
            dispatch('getQCData'),
            dispatch('getQCDataFromAPI'),
        ])
        return {
            appData,
            apiData,
            conflicts: detectConflicts(appData, apiData, apiData.deletedIds),
        }
    },
}

const mutations = {}
const getters = {}

export const qcchecker = {
    namespaced,
    state,
    getters,
    mutations,
    actions
}
